import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { useState, useEffect, useMemo } from 'react';
import { defineMessages, t, L10nProvider } from '@sm/intl';
import { SkeletonLoader } from '@wds/skeleton-loader';
import { Avatar } from '@wds/avatar';
import { Typography } from '@wds/typography';
import { useQuery, ApolloProvider } from '@apollo/client';
import { IconUser, IconUserDeleted, IconUsersGroup } from '@wds/icons';
import createBrowserApolloClient from '@sm/apollo-clients/dist/browser';
import { WrenchTheme } from '@wds/styles';
import { ThemeProvider } from 'react-jss';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

var daMessages = {
	"defineTranslations.DeletedUserIcon": "Slettet brugerikon",
	"defineTranslations.GroupUserIcon": "Ikon for bruger i arbejdsgruppe",
	"defineTranslations.GuestUserIcon": "Gæstebruger-ikon"
};

var deMessages = {
	"defineTranslations.DeletedUserIcon": "Icon für gelöschten Benutzer",
	"defineTranslations.GroupUserIcon": "Icon für Arbeitsgruppen-Benutzer",
	"defineTranslations.GuestUserIcon": "Gastbenutzersymbol"
};

var esMessages = {
	"defineTranslations.DeletedUserIcon": "Ícono de usuario eliminado",
	"defineTranslations.GroupUserIcon": "Ícono de usuario del grupo de trabajo",
	"defineTranslations.GuestUserIcon": "Ícono de usuario invitado"
};

var fiMessages = {
	"defineTranslations.DeletedUserIcon": "Poistettu käyttäjäkuvake",
	"defineTranslations.GroupUserIcon": "Työryhmän käyttäjäkuvake",
	"defineTranslations.GuestUserIcon": "Vieraskäyttäjäkuvake"
};

var frMessages = {
	"defineTranslations.DeletedUserIcon": "Icône Utilisateur supprimé",
	"defineTranslations.GroupUserIcon": "Icône Utilisateur du groupe de travail",
	"defineTranslations.GuestUserIcon": "Icône Utilisateur invité"
};

var itMessages = {
	"defineTranslations.DeletedUserIcon": "Icona utente eliminato",
	"defineTranslations.GroupUserIcon": "Icona utente gruppo di lavoro",
	"defineTranslations.GuestUserIcon": "Icona utente ospite"
};

var jaMessages = {
	"defineTranslations.DeletedUserIcon": "削除済みユーザーのアイコン",
	"defineTranslations.GroupUserIcon": "ワークグループユーザーのアイコン",
	"defineTranslations.GuestUserIcon": "ゲストユーザーのアイコン"
};

var koMessages = {
	"defineTranslations.DeletedUserIcon": "삭제된 사용자 아이콘",
	"defineTranslations.GroupUserIcon": "작업 그룹 사용자 아이콘",
	"defineTranslations.GuestUserIcon": "게스트 사용자 아이콘"
};

var nlMessages = {
	"defineTranslations.DeletedUserIcon": "Pictogram voor verwijderde gebruiker",
	"defineTranslations.GroupUserIcon": "Pictogram voor werkgroepgebruiker",
	"defineTranslations.GuestUserIcon": "Pictogram voor gastgebruiker"
};

var noMessages = {
	"defineTranslations.DeletedUserIcon": "Ikon for slettet bruker",
	"defineTranslations.GroupUserIcon": "Brukerikon for arbeidsgruppe",
	"defineTranslations.GuestUserIcon": "Ikon for gjestebruker"
};

var ptMessages = {
	"defineTranslations.DeletedUserIcon": "Ícone de usuário excluído",
	"defineTranslations.GroupUserIcon": "Ícone de usuário do grupo de projeto.",
	"defineTranslations.GuestUserIcon": "Ícone de usuário convidado"
};

var ruMessages = {
	"defineTranslations.DeletedUserIcon": "Значок удаленного пользователя",
	"defineTranslations.GroupUserIcon": "Значок пользователя команды",
	"defineTranslations.GuestUserIcon": "Значок гостя"
};

var svMessages = {
	"defineTranslations.DeletedUserIcon": "Ikon för borttagen användare",
	"defineTranslations.GroupUserIcon": "Ikon för arbetsgruppsanvändare",
	"defineTranslations.GuestUserIcon": "Ikon för gästanvändare"
};

var trMessages = {
	"defineTranslations.DeletedUserIcon": "Silinen kullanıcı simgesi",
	"defineTranslations.GroupUserIcon": "Çalışma grubu kullanıcı simgesi",
	"defineTranslations.GuestUserIcon": "Misafir kullanıcı simgesi"
};

var zhMessages = {
	"defineTranslations.DeletedUserIcon": "已刪除之使用者的圖示",
	"defineTranslations.GroupUserIcon": "工作群組使用者圖示",
	"defineTranslations.GuestUserIcon": "訪客使用者圖示"
};

const supportedLocals = [
    'en-US',
    'da-DK',
    'de-DE',
    'es-LA',
    'fi-FI',
    'fr-FR',
    'it-IT',
    'ja-JP',
    'ko-KR',
    'nl-NL',
    'no-NO',
    'pt-BR',
    'ru-RU',
    'sv-SE',
    'tr-TR',
    'zh-TW',
];
const supportedLocale = [
    {
        localeCode: 'en-US',
        localeMessages: {},
    },
    {
        localeCode: 'da-DK',
        localeMessages: daMessages,
    },
    {
        localeCode: 'de-DE',
        localeMessages: deMessages,
    },
    {
        localeCode: 'es-LA',
        localeMessages: esMessages,
    },
    {
        localeCode: 'fi-FI',
        localeMessages: fiMessages,
    },
    {
        localeCode: 'fr-FR',
        localeMessages: frMessages,
    },
    {
        localeCode: 'it-IT',
        localeMessages: itMessages,
    },
    {
        localeCode: 'ja-JP',
        localeMessages: jaMessages,
    },
    {
        localeCode: 'ko-KR',
        localeMessages: koMessages,
    },
    {
        localeCode: 'nl-NL',
        localeMessages: nlMessages,
    },
    {
        localeCode: 'no-NO',
        localeMessages: noMessages,
    },
    {
        localeCode: 'pt-BR',
        localeMessages: ptMessages,
    },
    {
        localeCode: 'ru-RU',
        localeMessages: ruMessages,
    },
    {
        localeCode: 'sv-SE',
        localeMessages: svMessages,
    },
    {
        localeCode: 'tr-TR',
        localeMessages: trMessages,
    },
    {
        localeCode: 'zh-TW',
        localeMessages: zhMessages,
    },
];
function localeMapper(localeCode) {
    const findLocale = supportedLocale.find((l) => l.localeCode.split('-')[0] === localeCode.split('-')[0]);
    if (!findLocale ||
        !supportedLocals.includes(findLocale.localeCode) ||
        findLocale.localeCode === 'en-US') {
        return {
            localeCode: 'en-US',
            localeMessages: {},
        };
    }
    return findLocale;
}

const flipObject = (obj) => {
    return Object.assign({}, ...Object.entries(obj).map(([k, v]) => ({ [v]: k })));
};

const SEMANTIC_COLOR_MAP = {
    '#6B787F': 'secondary',
    '#F05B24': 'warning',
    '#F48B64': 'warning',
    '#FDEBE5': 'warning',
    '#FBD8CA': 'warning',
    '#AC145A': 'error',
    '#D993B3': 'error',
    '#F5E3EB': 'error',
    '#EBC7D7': 'error',
    '#2DCCD3': 'info',
    '#E6F9FA': 'info',
    '#CDF3F4': 'info',
    '#F9BE00': 'upgrade',
    '#FEF2CC': 'upgrade',
    '#FDE599': 'upgrade',
    '#D0D2D3': 'muted',
    '#EDEEEE': 'muted',
    '#00BF6F': 'primary',
    '#E5F9F1': 'primary',
    '#ADEBD1': 'primary',
    '#000000': 'primary',
};
const COLOR_MAP = flipObject(SEMANTIC_COLOR_MAP);
function formatHexToSemantic(avatarDefaultColor) {
    return SEMANTIC_COLOR_MAP[avatarDefaultColor] || 'primary';
}

const formatInitials = (user) => {
    const { firstName, lastName, email } = user;
    if ((firstName === null || firstName === void 0 ? void 0 : firstName.length) && (lastName === null || lastName === void 0 ? void 0 : lastName.length)) {
        return `${[...firstName][0]}${[...lastName][0]}`.toLocaleUpperCase();
    }
    else if ((firstName === null || firstName === void 0 ? void 0 : firstName.length) && (firstName === null || firstName === void 0 ? void 0 : firstName.length) >= 2) {
        return `${[...firstName][0]}${[...firstName][1]}`.toLocaleUpperCase();
    }
    else if ((lastName === null || lastName === void 0 ? void 0 : lastName.length) && (lastName === null || lastName === void 0 ? void 0 : lastName.length) >= 2) {
        return `${[...lastName][0]}${[...lastName][1]}`.toLocaleUpperCase();
    }
    else if ((email === null || email === void 0 ? void 0 : email.length) && (email === null || email === void 0 ? void 0 : email.length) >= 2) {
        return `${[...email][0]}${[...email][1]}`.toLocaleUpperCase();
    }
    else if ((email === null || email === void 0 ? void 0 : email.length) === 1) {
        return `${[...email][0]}`.toLocaleUpperCase();
    }
    return ``;
};

const avatarFontSizeMap = {
    sm: 'caption',
    md: 'body',
    lg: 'sectionTitle',
    xl: 'hero2',
};
const AvatarBase = (_a) => {
    var _b;
    var { user, wrapInitials = true } = _a, rest = __rest(_a, ["user", "wrapInitials"]);
    const avatarDefaultColor = (_b = user === null || user === void 0 ? void 0 : user.avatarDefaultColor) !== null && _b !== void 0 ? _b : '#000000';
    const avatarBackgroundColor = formatHexToSemantic(avatarDefaultColor);
    const avatarFontSize = avatarFontSizeMap[(rest === null || rest === void 0 ? void 0 : rest.size) || 'sm'] || 'caption';
    return (jsx(Avatar, Object.assign({ color: avatarBackgroundColor }, rest, { children: wrapInitials ? jsx(Typography, { variant: avatarFontSize, children: formatInitials(user) }) : formatInitials(user) })));
};

const TRANSLATE_COPY = defineMessages({
    GUEST_USER_ICON: {
        id: 'defineTranslations.GuestUserIcon',
        defaultMessage: 'Guest user icon',
        description: '[Type: label][Vis: low] - aria label for the guest user icon',
    },
    DELETED_USER_ICON: {
        id: 'defineTranslations.DeletedUserIcon',
        defaultMessage: 'Deleted user icon',
        description: '[Type: label][Vis: low] - aria label for the deleted user icon',
    },
    WORKGROUP_ICON: {
        id: 'defineTranslations.GroupUserIcon',
        defaultMessage: 'Workgroup user icon',
        description: '[Type: label][Vis: low] - aria label for the workgroup user icon',
    },
});

const GetAvatarDocument = { "kind": "Document", "definitions": [{ "kind": "OperationDefinition", "operation": "query", "name": { "kind": "Name", "value": "getAvatar" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "user" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "email" } }, { "kind": "Field", "name": { "kind": "Name", "value": "firstName" } }, { "kind": "Field", "name": { "kind": "Name", "value": "lastName" } }, { "kind": "Field", "name": { "kind": "Name", "value": "avatarDefaultColor" } }] } }] } }] };

const AvatarComponent = (_a) => {
    var { user, isGuestUser = false, isWorkgroup = false, wrapInitials } = _a, rest = __rest(_a, ["user", "isGuestUser", "isWorkgroup", "wrapInitials"]);
    const [avatarUser, setAvatarUser] = useState(user);
    const [isGuest, setIsGuest] = useState(isGuestUser);
    const [userIsDeleted, setUserIsDeleted] = useState(!((avatarUser === null || avatarUser === void 0 ? void 0 : avatarUser.firstName) && (avatarUser === null || avatarUser === void 0 ? void 0 : avatarUser.email) && (avatarUser === null || avatarUser === void 0 ? void 0 : avatarUser.lastName)) && !isGuest && !isWorkgroup);
    const { data: avatarDetails, loading: avatarLoading, error, } = useQuery(GetAvatarDocument, {
        skip: !!avatarUser || isGuest || isWorkgroup,
    });
    useEffect(() => {
        if (!avatarUser && !isGuest && avatarDetails) {
            const formatAvatarDetails = (user) => {
                return Object.assign(Object.assign(Object.assign(Object.assign({}, ((user === null || user === void 0 ? void 0 : user.email) ? { email: user.email } : {})), ((user === null || user === void 0 ? void 0 : user.firstName) ? { email: user.firstName } : {})), ((user === null || user === void 0 ? void 0 : user.lastName) ? { email: user.lastName } : {})), ((user === null || user === void 0 ? void 0 : user.avatarDefaultColor) ? { email: user.avatarDefaultColor } : {}));
            };
            if (avatarDetails === null || avatarDetails === void 0 ? void 0 : avatarDetails.user) {
                const formattedAvatarDetails = formatAvatarDetails(avatarDetails.user);
                setAvatarUser(formattedAvatarDetails);
            }
            else {
                setIsGuest(true);
            }
        }
    }, [avatarDetails, avatarUser, isGuest]);
    useEffect(() => {
        setAvatarUser(user);
    }, [user]);
    useEffect(() => {
        setIsGuest(isGuestUser);
    }, [isGuestUser]);
    useEffect(() => {
        if (!(avatarUser === null || avatarUser === void 0 ? void 0 : avatarUser.firstName) && !(avatarUser === null || avatarUser === void 0 ? void 0 : avatarUser.email) && !(avatarUser === null || avatarUser === void 0 ? void 0 : avatarUser.lastName) && !isGuest && !isWorkgroup) {
            setUserIsDeleted(true);
        }
        else {
            setUserIsDeleted(false);
        }
    }, [avatarUser, isGuest, isWorkgroup]);
    const avatarLoadingError = () => {
        return (jsx(SkeletonLoader, { width: 40, height: 40, children: jsx("circle", { cx: "16.5", cy: "16.5", r: "16.5" }) }));
    };
    const guestUser = () => {
        return (jsx(Avatar, Object.assign({ color: "muted" }, rest, { children: jsx(IconUser, { color: "secondary", title: t(TRANSLATE_COPY.GUEST_USER_ICON) }) })));
    };
    const deletedUser = () => {
        return (jsx(Avatar, Object.assign({ color: "muted" }, rest, { children: jsx(IconUserDeleted, { color: "secondary", title: t(TRANSLATE_COPY.DELETED_USER_ICON) }) })));
    };
    const renderWorkgroupAvatar = () => {
        return (jsx(Avatar, Object.assign({ color: "muted" }, rest, { children: jsx(IconUsersGroup, { color: "secondary", title: t(TRANSLATE_COPY.WORKGROUP_ICON) }) })));
    };
    return (jsxs(Fragment, { children: [(avatarLoading || error) && avatarLoadingError(), isGuest && guestUser(), userIsDeleted && deletedUser(), isWorkgroup && renderWorkgroupAvatar(), avatarUser && !isGuest && !userIsDeleted && !isWorkgroup && (jsx(AvatarBase, Object.assign({ user: avatarUser, wrapInitials: wrapInitials }, rest)))] }));
};

const setApolloClient = (graphQLClientConfig) => {
    if (!graphQLClientConfig.graphQLUri) {
        throw new Error('GraphQL Uri not provided');
    }
    return createBrowserApolloClient({
        uri: graphQLClientConfig.graphQLUri,
        cacheHydration: {},
        pageRequestId: graphQLClientConfig.pageRequestId,
        linkOptions: {
            credentials: 'include',
            batchInterval: 30,
        },
        metadata: {
            appName: graphQLClientConfig.appName,
            appVersion: graphQLClientConfig.appVersion,
        },
        availableLoggedOutPaths: [],
    });
};

const UserAvatarWrapper = (_a) => {
    var { graphQLClientConfig, localeCode } = _a, props = __rest(_a, ["graphQLClientConfig", "localeCode"]);
    const component = useMemo(() => {
        if (localeCode) {
            const localeMapped = localeMapper(localeCode);
            return (jsx(L10nProvider, { localeCode: localeMapped.localeCode, localeMessages: localeMapped.localeMessages, children: jsx(AvatarComponent, Object.assign({}, props)) }));
        }
        return jsx(AvatarComponent, Object.assign({}, props));
    }, [localeCode, props]);
    if (!graphQLClientConfig) {
        return component;
    }
    const apolloClient = setApolloClient(graphQLClientConfig);
    return (jsx(ThemeProvider, { theme: WrenchTheme, children: jsx(ApolloProvider, { client: apolloClient, children: component }) }));
};

export { AvatarBase, COLOR_MAP, UserAvatarWrapper as UserAvatar };
